import {
  toggleMarkItem,
  insertItem,
  wrapItem,
  Dropdown,
  undoItem,
  redoItem,
  wrapListItem,
  icons,
  MenuItem,
} from 'prosemirror/menu';
import { FieldPrompt, TextField } from 'prosemirror/ui';
import { commands } from 'prosemirror/edit';
import schema from './schema';
import EmbedModal from './EmbedModal';

const sinkListItem = commands.sinkListItem(schema.nodes.list_item);
const liftListItem = commands.liftListItem(schema.nodes.list_item);

const unindentIcon = {
  type: 'icon',
  width: 1024,
  height: 1024,
  // eslint-disable-next-line max-len
  path: 'M219 310v329q0 7-5 12t-12 5q-8 0-13-5l-164-164q-5-5-5-13t5-13l164-164q5-5 13-5 7 0 12 5t5 12zM1024 749v109q0 7-5 12t-12 5h-987q-7 0-12-5t-5-12v-109q0-7 5-12t12-5h987q7 0 12 5t5 12zM1024 530v109q0 7-5 12t-12 5h-621q-7 0-12-5t-5-12v-109q0-7 5-12t12-5h621q7 0 12 5t5 12zM1024 310v109q0 7-5 12t-12 5h-621q-7 0-12-5t-5-12v-109q0-7 5-12t12-5h621q7 0 12 5t5 12zM1024 91v109q0 7-5 12t-12 5h-987q-7 0-12-5t-5-12v-109q0-7 5-12t12-5h987q7 0 12 5t5 12z',
};

const underlineIcon = {
  width: 1792,
  height: 1792,
  // eslint-disable-next-line max-len
  path: 'M176 223q-37-2-45-4l-3-88q13-1 40-1 60 0 112 4 132 7 166 7 86 0 168-3 116-4 146-5 56 0 86-2l-1 14 2 64v9q-60 9-124 9-60 0-79 25-13 14-13 132 0 13 .5 32.5t.5 25.5l1 229 14 280q6 124 51 202 35 59 96 92 88 47 177 47 104 0 191-28 56-18 99-51 48-36 65-64 36-56 53-114 21-73 21-229 0-79-3.5-128t-11-122.5-13.5-159.5l-4-59q-5-67-24-88-34-35-77-34l-100 2-14-3 2-86h84l205 10q76 3 196-10l18 2q6 38 6 51 0 7-4 31-45 12-84 13-73 11-79 17-15 15-15 41 0 7 1.5 27t1.5 31q8 19 22 396 6 195-15 304-15 76-41 122-38 65-112 123-75 57-182 89-109 33-255 33-167 0-284-46-119-47-179-122-61-76-83-195-16-80-16-237v-333q0-188-17-213-25-36-147-39zm1488 1409v-64q0-14-9-23t-23-9h-1472q-14 0-23 9t-9 23v64q0 14 9 23t23 9h1472q14 0 23-9t9-23z',
};

const history = [undoItem, redoItem];

function toggleMark(name, config) {
  return toggleMarkItem(schema.marks[name], { icon: icons[name], ...config });
}

function wrapIn(name, config) {
  return wrapItem(schema.nodes[name], { icon: icons[name], ...config });
}

function wrapInList(name, label) {
  return wrapListItem(schema.nodes[name], { label });
}

function promptLinkAttrs(pm, callback) {
  new FieldPrompt(pm, 'Create a link', {
    href: new TextField({
      label: 'Link target',
      required: true,
      clean: (val) => {
        if (!/^https?:\/\//i.test(val)) {
          return `http://${val}`;
        }
        return val;
      },
    }),
    title: new TextField({ label: 'Title' }),
  }).open(callback);
}

const inline = [
  toggleMark('strong'),
  toggleMark('em'),
  toggleMark('u', { icon: underlineIcon, class: 'pm-underline' }),
  toggleMark('sup', { label: 's', class: 'pm-sup-menu-item' }),
  toggleMark('sub', { label: 's', class: 'pm-sub-menu-item' }),
  toggleMark('link', { attrs: promptLinkAttrs }),
];

const limitedInline = [
  toggleMark('em'),
  toggleMark('sup', { label: 's', class: 'pm-sup-menu-item' }),
  toggleMark('sub', { label: 's', class: 'pm-sub-menu-item' }),
];

const embed = [
  insertItem(schema.nodes.amItem, {
    label: 'Embed',
    class: 'pm-embed-menu-item',
    attrs(pm, done) {
      const { node } = pm.selection;
      if (node && node.type) {
        pm.requestNodeModal(EmbedModal, {
          data: node.attrs,
          update: done,
        });
      } else {
        pm.requestNodeModal(EmbedModal, { update: done });
      }
    },
  }),
];

const block = [
  new MenuItem({
    title: 'Indent list item',
    run: sinkListItem,
    select: (pm) => sinkListItem(pm, false),
    icon: unindentIcon,
    class: 'pm-indent-menu-item',
  }),
  new MenuItem({
    title: 'Unindent list item',
    run: liftListItem,
    select: (pm) => liftListItem(pm, false),
    icon: unindentIcon,
    class: 'pm-unindent-menu-item',
  }),
  wrapListItem(schema.nodes.bullet_list, {
    title: 'Wrap in bullet list',
    icon: icons.bulletList,
  }),
  new Dropdown(
    [
      wrapInList('ordered_list', '1. 2. 3.'),
      wrapInList('lower_roman_list', 'i. ii. iii.'),
      wrapInList('upper_roman_list', 'I. II. III.'),
      wrapInList('lower_alpha_list', 'a. b. c.'),
      wrapInList('upper_alpha_list', 'A. B. C.'),
    ],
    { label: '1. 2. 3.' },
  ),

  wrapIn('blockquote'),
];

export default {
  default: [inline, embed, block, history],
  feedback: [inline, block, history],
  student: [inline, block, history],
  title: [limitedInline, history],
};
